/**
 * @spec docs/specs/features/staking-position-ui.md#6-network-switching-and-hardware-wallets
 * Classifies a Privy ConnectedWallet by how it reaches the dapp, so network-switch failures can
 * carry the one instruction that actually unblocks the user. Pure functions — no React.
 */

export type WalletKind = 'embedded' | 'injected' | 'wallet_connect' | 'unknown';

/**
 * Structural subset of Privy's ConnectedWallet. Kept loose on purpose so tests can pass plain
 * objects and so this module does not chase Privy's exported type names across versions.
 */
export interface WalletLike {
  walletClientType?: string;
  connectorType?: string;
  meta?: { name?: string; id?: string } | null;
}

export function getWalletKind(wallet: WalletLike | null | undefined): WalletKind {
  if (!wallet) return 'unknown';
  if (wallet.walletClientType === 'privy') return 'embedded';
  if (wallet.connectorType === 'wallet_connect_v2' || wallet.connectorType === 'wallet_connect') {
    return 'wallet_connect';
  }
  if (wallet.connectorType === 'injected') return 'injected';
  return 'unknown';
}

/**
 * Ledger is detectable only where the connector exposes it: the WalletConnect peer name
 * ("Ledger Wallet") or a Ledger-branded client type. A Ledger account driven through MetaMask
 * is indistinguishable from a plain MetaMask account — callers must not rely on a false here.
 */
export function isLikelyLedger(wallet: WalletLike | null | undefined): boolean {
  if (!wallet) return false;
  const haystack = [wallet.walletClientType, wallet.meta?.name, wallet.meta?.id]
    .filter((s): s is string => typeof s === 'string' && s.length > 0)
    .join(' ');
  return /ledger/i.test(haystack);
}

/** The manual step a user can take when the dapp's programmatic switch did not land. */
export function describeManualSwitch(kind: WalletKind, chainName: string, ledger: boolean): string {
  switch (kind) {
    case 'wallet_connect':
      // A WalletConnect session cannot gain a chain after pairing; only a fresh pairing can.
      return ledger
        ? `Your Ledger Live session does not include ${chainName}. In Ledger Live, add a ${chainName} account for this address, then disconnect and reconnect selecting that account.`
        : `Your WalletConnect session does not include ${chainName}. Disconnect, then reconnect and select a ${chainName} account.`;
    case 'injected':
      return `Switch to ${chainName} in your wallet extension, then try again.`;
    case 'embedded':
      return `Your Phenom wallet should switch automatically. Please try again.`;
    default:
      return `Switch to ${chainName} inside your wallet, then try again.`;
  }
}
